if (!customElements.get('price-list')) {
  customElements.define(
    'price-list',
    class PriceList extends HTMLElement {
      static COLOR_ORDER = {
        Mousserend: 1,
        Wit: 2,
        Rosé: 3,
        Rood: 4,
      };

      static FALLBACK_VALUE = 'Onbekend';

      static MONTHS_NL = [
        'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
        'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December',
      ];

      constructor() {
        super();
        this.container = null;
        this.pdfButton = null;
        this.translations = null;
        this.allProducts = [];
        this.config = null;
      }

      connectedCallback() {
        this.initializeElements();
        this.setupEventListeners();
        this.loadConfig();
      }

      disconnectedCallback() {
        if (this.pdfButton && this.handlePdfExportBound) {
          this.pdfButton.removeEventListener('click', this.handlePdfExportBound);
        }
      }

      initializeElements() {
        this.container = this.querySelector('[data-pricelist-content]');
        this.pdfButton = this.querySelector('[data-pricelist-export-pdf-btn]');
      }

      setupEventListeners() {
        if (this.pdfButton) {
          this.handlePdfExportBound = this.handlePdfExport.bind(this);
          this.pdfButton.addEventListener('click', this.handlePdfExportBound);
        }
      }

      loadConfig() {
        if (!this.container) {
          console.error('Pricelist: Content container not found');
          return;
        }

        try {
          const configElement = document.getElementById('pricelist-config');
          const paginationElement = document.getElementById('pricelist-pagination');

          if (!configElement || !paginationElement) {
            this.showErrorState();
            return;
          }

          this.config = JSON.parse(configElement.textContent);
          this.translations = this.config.translations;
          this.pagination = JSON.parse(paginationElement.textContent);

          this.loadAllProductPages();
        } catch (error) {
          console.error('Pricelist: Error loading config', error);
          this.showErrorState();
        }
      }

      async loadAllProductPages() {
        try {
          const products = [];

          // Show loading state
          this.showLoadingState();

          // Load first page from pre-rendered JSON
          const firstPageElement = document.getElementById(
            `pricelist-products-page-${this.pagination.currentPage}`
          );
          if (firstPageElement) {
            const firstPageProducts = JSON.parse(firstPageElement.textContent);
            products.push(...firstPageProducts.map((p) => this.transformProduct(p)));
          }

          // Fetch remaining pages via AJAX if needed
          if (this.pagination.totalPages > 1) {
            for (let page = 2; page <= this.pagination.totalPages; page++) {
              const pageProducts = await this.fetchProductPage(page);
              if (pageProducts && pageProducts.length > 0) {
                products.push(...pageProducts.map((p) => this.transformProduct(p)));
              }
            }
          }

          this.allProducts = products;

          if (this.allProducts.length === 0) {
            console.warn('Pricelist: No products found');
            this.showEmptyState();
            return;
          }

          this.renderPricelist();
        } catch (error) {
          console.error('Pricelist: Error loading products', error);
          this.showErrorState();
        }
      }

      async fetchProductPage(page) {
        try {
          const url = `${this.pagination.baseUrl}?page=${page}`;
          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const pageElement = doc.getElementById(`pricelist-products-page-${page}`);

          if (!pageElement) {
            console.warn(`Pricelist: No products found for page ${page}`);
            return [];
          }

          return JSON.parse(pageElement.textContent);
        } catch (error) {
          console.error(`Pricelist: Error fetching page ${page}`, error);
          return [];
        }
      }

      transformProduct(product) {
        // Products are already pre-transformed from Liquid with correct B2B pricing
        // Just ensure fallback values and URL are set
        return {
          id: product.id,
          title: product.title,
          url: `/products/${product.handle}`,
          price: product.price,
          sku: product.sku || '',
          vendor: product.vendor || PriceList.FALLBACK_VALUE,
          image: {
            src: product.image?.src || this.config.fallbackImage,
            srcset: product.image?.srcset,
            alt: product.image?.alt || product.title,
            noImage: product.image?.noImage || false,
          },
          metafields: {
            country: product.metafields?.country || PriceList.FALLBACK_VALUE,
            region: product.metafields?.region || PriceList.FALLBACK_VALUE,
            vintage: product.metafields?.vintage,
            color: product.metafields?.color || null,
          },
        };
      }

      renderPricelist() {
        // Clear loading state
        this.container.innerHTML = '';

        // Set dynamic date on cover page
        this.setCoverDate();

        const grouped = this.groupProductsByLocation(this.allProducts);

        // Generate table of contents
        this.renderTableOfContents(grouped);
        this.renderGroupedProducts(grouped);
      }

      setCoverDate() {
        const dateEl = this.querySelector('[data-pricelist-cover-date]');
        if (dateEl) {
          const now = new Date();
          dateEl.textContent = `${PriceList.MONTHS_NL[now.getMonth()]} ${now.getFullYear()}`;
        }
      }

      generateSectionId(country, region, vendor) {
        return `section-${[country, region, vendor]
          .join('-')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '')}`;
      }

      renderTableOfContents(groupedProducts) {
        const tocEl = this.querySelector('[data-pricelist-toc]');
        if (!tocEl) return;

        const sortedCountries = Object.keys(groupedProducts).sort();
        let html =
          '<h2 class="pricelist__toc-title">' +
          (this.translations.toc || 'Inhoudsopgave') +
          '</h2>';
        html += '<ul class="pricelist__toc-list">';

        for (const country of sortedCountries) {
          html += `<li class="pricelist__toc-country">${country}`;
          const regions = groupedProducts[country];
          const sortedRegions = Object.keys(regions).sort();
          html += '<ul>';

          for (const region of sortedRegions) {
            html += `<li class="pricelist__toc-region">${region}`;
            const vendors = regions[region];
            const sortedVendors = Object.keys(vendors).sort();
            html += '<ul>';

            for (const vendor of sortedVendors) {
              const sectionId = this.generateSectionId(country, region, vendor);
              html += `<li class="pricelist__toc-vendor"><a href="#${sectionId}">${vendor}</a></li>`;
            }

            html += '</ul></li>';
          }

          html += '</ul></li>';
        }

        html += '</ul>';
        tocEl.innerHTML = html;
      }

      groupProductsByLocation(products) {
        return products.reduce((grouped, product) => {
          const country = product.metafields?.country || PriceList.FALLBACK_VALUE;
          const region = product.metafields?.region || PriceList.FALLBACK_VALUE;
          const vendor = product.vendor || PriceList.FALLBACK_VALUE;

          if (!grouped[country]) grouped[country] = {};
          if (!grouped[country][region]) grouped[country][region] = {};
          if (!grouped[country][region][vendor]) grouped[country][region][vendor] = [];

          grouped[country][region][vendor].push(product);
          return grouped;
        }, {});
      }

      sortProductsByColor(products) {
        return products.sort((a, b) => {
          const colorA = a.metafields?.color || '';
          const colorB = b.metafields?.color || '';
          const orderA = PriceList.COLOR_ORDER[colorA] || 999;
          const orderB = PriceList.COLOR_ORDER[colorB] || 999;
          return orderA - orderB;
        });
      }

      renderGroupedProducts(groupedProducts) {
        const htmlFragments = [];
        const sortedCountries = Object.keys(groupedProducts).sort();

        for (const country of sortedCountries) {
          const regions = groupedProducts[country];
          const sortedRegions = Object.keys(regions).sort();

          for (const region of sortedRegions) {
            const vendors = regions[region];
            const sortedVendors = Object.keys(vendors).sort();

            for (const vendor of sortedVendors) {
              const products = vendors[vendor];
              this.sortProductsByColor(products);
              htmlFragments.push(
                this.renderProductTable(country, region, vendor, products)
              );
            }
          }
        }

        this.container.innerHTML = htmlFragments.join('');
      }

      renderProductTable(country, region, vendor, products) {
        const tableRows = products
          .map((product) => this.renderProductRow(product))
          .join('');
        const sectionId = this.generateSectionId(country, region, vendor);

        return `
          <h2 class="pricelist__title" id="${sectionId}">${country} - ${region} - ${vendor}</h2>
          <table class="pricelist__table">
            <thead>
              <tr>
                <th class="pricelist__actions">
                  <span class="visually-hidden">${this.translations.actions}</span>
                </th>
                <th class="pricelist__color-col">${this.translations.color}</th>
                <th>${this.translations.productName}</th>
                <th class="pricelist__sku">${this.translations.sku}</th>
                <th>${this.translations.vintage}</th>
                <th>${this.translations.price}</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        `;
      }

      renderProductRow(product) {
        const price = product.price || 'NV';
        const vintage =
          (product.metafields?.vintage ?? '') === '0'
            ? ''
            : (product.metafields?.vintage ?? '');
        const sku = product.sku || '';
        const imageUrl = product.image?.src || '';
        const imageSrcset = product.image?.srcset || '';
        const imageAlt = product.image?.alt || product.title;

        const colorLabel = product.metafields?.color || '';

        // The leading cell is an inert slot the bestellijst app's theme extension mounts
        // its curator toggle into; pricelist.css hides the column for everyone else.
        return `
          <tr class="pricelist-product" data-product-id="${product.id}">
            <td class="pricelist__actions">
              <div
                data-bestellijst-slot
                data-product-id="${product.id}"
                data-bestellijst-class="bestellijst-button--row"
              ></div>
            </td>
            <td class="pricelist__color-col">${colorLabel}</td>
            <td class="pricelist__name">
              <div class="media media--transparent${product.image.noImage ? ' media--fallback' : ''}">
                <img
                  class="pricelist__image motion-reduce"
                  srcset="${imageSrcset}"
                  src="${imageUrl}"
                  alt="${imageAlt}"
                  width="40"
                  height="40"
                  loading="lazy"
                >
              </div>
              <a href="${product.url}" class="pricelist__link">
                ${product.title}
              </a>
            </td>
            <td class="pricelist__sku">${sku}</td>
            <td>${vintage}</td>
            <td>${price}</td>
          </tr>
        `;
      }

      showLoadingState() {
        this.container.innerHTML = `
          <div class="pricelist__loading">
            <div class="pricelist__loader"> </div>
          </div>
        `;
      }

      showEmptyState() {
        this.container.innerHTML = `
          <div class="pricelist__empty">
            <p>${this.translations.emptyState}</p>
          </div>
        `;
      }

      showErrorState() {
        this.container.innerHTML = `
          <div class="pricelist__empty">
            <p>${this.translations.errorState}</p>
          </div>
        `;
      }

      // ---- PDF Export (lazy-loads pricelist-pdf.js) ----

      async handlePdfExport() {
        const buttonLabel = this.pdfButton.textContent;
        this.pdfButton.disabled = true;
        this.pdfButton.textContent = 'Genereren...';

        try {
          await this.loadPdfScript();

          const now = new Date();
          const monthYear = `${PriceList.MONTHS_NL[now.getMonth()]} ${now.getFullYear()}`;
          const grouped = this.groupProductsByLocation(this.allProducts);

          const exporter = new window.PricelistPdfExporter({
            groupedProducts: grouped,
            translations: this.translations,
            logoUrl: this.querySelector('.pricelist__cover-logo')?.src || '',
            textPageHtml: this.querySelector('.pricelist__text-page .rte')?.innerHTML?.trim() || '',
            colorOrder: PriceList.COLOR_ORDER,
            monthYear,
            stylesUrl: this.getAttribute('data-pdf-styles'),
            initUrl: this.getAttribute('data-pdf-init'),
          });

          exporter.export();
        } catch (error) {
          console.error('Pricelist: PDF export failed', error);
        } finally {
          this.pdfButton.disabled = false;
          this.pdfButton.textContent = buttonLabel;
        }
      }

      loadPdfScript() {
        if (window.PricelistPdfExporter) return Promise.resolve();

        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = this.getAttribute('data-pdf-script');
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load PDF script'));
          document.head.appendChild(script);
        });
      }
    }
  );
}
